import type { Dictionary } from './uz';

/**
 * Interface copy, Russian. Complete on day one — a half-translated admin is
 * the same failure in a new place (§10.8). SEO work happens in Russian, so
 * this is the language most of the team will actually read.
 */
export const ru: Dictionary = {
  brand: { name: 'iPost', suffix: 'панель управления' },

  nav: {
    overview: 'Панель',
    articles: 'Новости',
    services: 'Услуги',
    stories: 'Истории',
    pages: 'Страницы',
    media: 'Изображения',
    categories: 'Категории',
    redirects: 'Перенаправления',
    leads: 'Обращения',
    scans: 'Сканирования RS',
    settings: 'Настройки',
    users: 'Пользователи',
    audit: 'История изменений',
    groupApp: 'Приложение',
    groupContent: 'Контент',
    groupManage: 'Управление',
    groupSystem: 'Система',
  },

  common: {
    save: 'Сохранить',
    saving: 'Сохраняется…',
    saved: 'Сохранено',
    cancel: 'Отмена',
    close: 'Закрыть',
    delete: 'Удалить',
    deleted: 'Удалено',
    restore: 'Вернуть',
    edit: 'Изменить',
    create: 'Добавить',
    search: 'Поиск',
    filter: 'Фильтр',
    all: 'Все',
    none: 'Нет',
    yes: 'Да',
    no: 'Нет',
    back: 'Назад',
    next: 'Дальше',
    prev: 'Назад',
    loading: 'Загрузка',
    retry: 'Повторить',
    copy: 'Копировать',
    copied: 'Скопировано',
    undo: 'Вернуть',
    optional: 'необязательно',
    required: 'обязательно',
    moreActions: 'Другие действия',
    collapseSidebar: 'Свернуть меню',
    expandSidebar: 'Развернуть меню',
    openMenu: 'Меню',
    unknown: 'неизвестно',
    of: 'из',
    openOnSite: 'Открыть на сайте',
    details: 'Подробнее',
  },

  status: {
    draft: 'Черновик',
    published: 'Опубликовано',
    live: 'На сайте',
    liveOn: 'Видно на сайте',
    notLive: 'Пока не на сайте',
  },

  auth: {
    signInTitle: 'Панель управления',
    signInSubtitle: 'Контент и SEO',
    email: 'Эл. почта',
    login: 'Логин',
    password: 'Пароль',
    showPassword: 'Показать пароль',
    hidePassword: 'Скрыть пароль',
    signIn: 'Войти',
    signingIn: 'Вход…',
    signOut: 'Выйти',
    changePasswordTitle: 'Смените пароль',
    changePasswordWhy:
      'Вам выдали временный пароль. Придумайте свой, прежде чем продолжить.',
    currentPassword: 'Текущий пароль',
    newPassword: 'Новый пароль',
    repeatPassword: 'Повторите новый пароль',
    passwordsDiffer: 'Пароли не совпадают',
    changePassword: 'Сменить пароль',
    passwordChanged: 'Пароль изменён',
  },

  overview: {
    title: 'Панель управления',
    greetingMorning: 'Доброе утро',
    greetingDay: 'Добрый день',
    greetingEvening: 'Добрый вечер',
    attention: 'Требует внимания',
    nothingToDo: 'Сейчас всё в порядке',
    nothingToDoHint: 'Черновиков нет, публикации заполнены полностью.',
    drafts: 'незаконченный черновик',
    missingRu: 'новость без русского текста',
    missingEn: 'новость без английского перевода',
    missingSeo: 'публикация без текста для Google',
    noCover: 'публикация без обложки',
    unreadLeads: 'непрочитанное обращение',
    recent: 'Последние изменения',
    noRecent: 'Изменений пока нет',
    quickActions: 'Быстрые действия',
    newArticle: 'Добавить новость',
    openMedia: 'Библиотека изображений',
    system: 'Состояние системы',
    storage: 'Хранилище файлов',
    storageOk: 'работает',
    storageDown: 'недоступно',
    mediaCount: 'Изображения',
    mediaUnused: 'Неиспользуемые',
    published: 'Опубликовано на сайте',
  },

  articles: {
    title: 'Новости',
    subtitle: 'Раздел новостей на сайте',
    new: 'Добавить новость',
    emptyTitle: 'Новостей пока нет',
    emptyHint: 'Добавьте первую новость — она сразу появится на сайте.',
    emptyAction: 'Добавьте первую новость',
    noResultsTitle: 'Ничего не найдено',
    noResultsHint: 'Попробуйте другое слово или снимите фильтр.',
    columnTitle: 'Заголовок',
    columnLanguages: 'Языки',
    columnCategory: 'Категория',
    columnUpdated: 'Изменено',
    columnStatus: 'Статус',
    untitled: 'Без заголовка',
    deleteConfirm: 'Удалить эту новость?',
    deleteConfirmBody: 'Новость исчезнет с сайта. Её можно будет вернуть.',
    filterAll: 'Все',
    filterDrafts: 'Черновики',
    filterPublished: 'Опубликованные',
    filterMissingRu: 'Без русского',
    filterMissingSeo: 'SEO не заполнено',
    // "4 новостей" / "2 новости" / "1 новость" — a numeral-agreement rule that
    // is not worth a plural engine for one string.
    total: (n: number) => {
      const mod10 = n % 10;
      const mod100 = n % 100;
      if (mod10 === 1 && mod100 !== 11) return `${n} новость`;
      if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} новости`;
      return `${n} новостей`;
    },
  },

  editor: {
    backToList: 'Новости',
    newTitle: 'Новая новость',
    contentGroup: 'Текст',
    addressGroup: 'Адрес страницы',
    searchGroup: 'Как выглядит в Google',
    socialGroup: 'При отправке в соцсети',
    publishingGroup: 'Публикация',
    advancedGroup: 'Настройки поисковых систем',

    titleLabel: 'Заголовок',
    titlePlaceholder: 'О чём новость?',
    bodyLabel: 'Текст',
    bodyPlaceholder: 'Начните писать здесь…',

    slugLabel: 'Адрес страницы',
    slugHint: 'Этот адрес виден в браузере и в Google.',
    slugFromTitle: 'Взять из заголовка',
    slugRenameWarning: 'Старый адрес будет перенаправлен на новый:',
    slugTaken: 'Этот адрес уже занят другой новостью. Выберите другой.',
    slugRequired: 'Сначала введите заголовок или адрес страницы.',

    metaTitleLabel: 'Заголовок для Google',
    metaTitleHint: 'Если оставить пустым, возьмётся заголовок новости.',
    metaDescLabel: 'Короткое описание для Google',
    metaDescHint: 'Именно его человек читает в списке результатов.',
    metaKeywordsLabel: 'Ключевые слова',
    metaKeywordsHint: 'Через запятую. До 10 штук.',
    metaKeywordsPlaceholder: 'грузоперевозки из китая, авиадоставка, таможня',
    metaTooLong: 'Google обрежет этот текст',
    metaGood: 'Хорошая длина',
    metaEmpty: 'Не заполнено',

    coverLabel: 'Обложка',
    coverEmpty: 'Выберите изображение',
    coverChange: 'Заменить',
    coverRemove: 'Убрать',
    altLabel: 'Описание изображения',
    altHint: 'Его читают незрячие пользователи и Google.',
    altMissing: 'Описание изображения не заполнено',

    ogLabel: 'Изображение для соцсетей',
    ogHint: 'Показывается в Telegram и соцсетях. Если пусто — берётся обложка.',

    categoryLabel: 'Категория',
    categoryNone: 'Не выбрана',
    publishedAtLabel: 'Дата публикации',
    canonicalLabel: 'Основной адрес',
    canonicalHint:
      'Если этот текст есть и на другом сайте, укажите оригинал. Обычно оставляют пустым.',
    noIndexLabel: 'Не добавлять в Google',
    noIndexHint: 'Страница останется на сайте, но не появится в поиске.',

    preview: 'Посмотреть',
    previewHint: 'Посмотреть, как получится на сайте, не публикуя',
    previewOpening: 'Открывается…',
    publish: 'Опубликовать',
    publishing: 'Публикуется…',
    published: 'Опубликовано',
    saveOk: 'Успешно сохранено',
    unpublish: 'Снять с сайта',
    unpublishHint: 'Страница исчезнет с сайта и снова станет черновиком',
    unpublished: 'Снято с сайта',
    lastSaved: 'сохранено',
    neverSaved: 'ещё не сохранялось',
    unsaved: 'есть несохранённые изменения',

    readyTitle: 'Готово к публикации',
    readyAllGood: 'Всё на месте',
    blockersTitle: 'Для публикации не хватает',
    warningsTitle: 'Обратите внимание',

    conflictTitle: 'Эту новость изменил другой пользователь',
    conflictBody: 'внёс изменения. Какой вариант оставляем?',
    conflictKeepMine: 'Оставить мой',
    conflictTakeTheirs: 'Оставить их',
    conflictOpenNew: 'Открыть в новой вкладке',

    revalidateFailed:
      'Новость на сайте, но кэш страницы не обновился. Он обновится сам через несколько минут.',
    revalidateRetry: 'Повторить',

    tools: {
      bold: 'Полужирный',
      italic: 'Курсив',
      h2: 'Заголовок',
      h3: 'Подзаголовок',
      bullets: 'Маркированный список',
      numbers: 'Нумерованный список',
      quote: 'Цитата',
      link: 'Добавить ссылку',
      image: 'Вставить изображение',
      video: 'Вставить видео',
    },
    linkTitle: 'Ссылка',
    linkHint: 'Внутренний адрес (/ru/services) или полный (https://…)',
    linkAddress: 'Адрес',
    linkRemove: 'Убрать ссылку',
    videoTitle: 'Вставить видео',
    videoHint: 'Вставьте ссылку YouTube, Vimeo или Instagram — на сайте появится видео.',
    videoAddress: 'Ссылка на видео',
    videoInvalid: 'Неверная ссылка. Нужен YouTube/Vimeo/Instagram или полная https-ссылка.',
    wordCount: (n: number) => `${n} слов`,
    readingTime: (n: number) => `~${n} мин чтения`,
  },

  help: {
    slug: {
      title: 'Что такое адрес страницы',
      what: 'Это часть ссылки, которую видно в адресной строке браузера и в результатах Google.',
      how: 'Только строчные латинские буквы, цифры и дефис. Без пробелов, кириллицы и знаков.',
      exampleLabel: 'Например',
      exampleFrom: 'Открыто новое направление из Китая',
      exampleTo: 'novoe-napravlenie-iz-kitaya',
      tips: [
        '3–6 слов достаточно. Длинный адрес обрезается в выдаче.',
        'Включите главное слово — «napravlenie», «aviadostavka».',
        'Не добавляйте даты и номера: 2026 устареет через год.',
      ],
      warn: 'Если изменить адрес опубликованной страницы, старый автоматически перенаправится на новый. Старые ссылки продолжат работать.',
    },
    metaTitle: {
      title: 'Заголовок для Google',
      what: 'Синяя кликабельная строка в результатах поиска. Она же показывается во вкладке браузера.',
      how: '50–60 символов — оптимально. После 70 Google обрежет заголовок многоточием.',
      exampleLabel: 'Хороший пример',
      exampleFrom: 'Новость о новом направлении',
      exampleTo: 'Прямая доставка из Китая в Ташкент — iPost',
      tips: [
        'Главное слово — в начало: человек читает первые слова.',
        'В конце добавьте «— iPost», чтобы бренд запоминался.',
        'На каждой странице свой заголовок. Повторы путают Google.',
      ],
      warn: 'Если оставить пустым, возьмётся заголовок новости. Это работает, но отдельно написанный почти всегда лучше.',
    },
    metaDesc: {
      title: 'Короткое описание для Google',
      what: 'Две серые строки под заголовком. Именно их человек читает перед тем, как нажать.',
      how: '140–160 символов. Максимум — 180.',
      exampleLabel: 'Хороший пример',
      exampleFrom: 'В этой новости рассказывается о новом направлении.',
      exampleTo: 'Прямое направление из Китая в Ташкент запущено. Срок доставки сократился до 4 дней. Рассчитайте стоимость.',
      tips: [
        'Напишите, что вы предлагаете и что нужно сделать.',
        'Не повторяйте заголовок — это дополнительное место, не тратьте его зря.',
        'Цифры и сроки вызывают доверие: «4 дня», «12–18 дней».',
      ],
      warn: 'Этот текст не влияет на позицию напрямую, но заметно повышает кликабельность.',
    },
    metaKeywords: {
      title: 'Ключевые слова — как есть',
      what: 'Это мета-тег «keywords». Google не читает его с 2009 года, Яндекс тоже не использует его для ранжирования.',
      how: 'Перечислите через запятую. Не больше 10 — иначе тег читается как спам.',
      exampleLabel: 'Если всё же заполняете',
      exampleFrom: 'груз, доставка, китай, авиа, быстро, дёшево, лучший, цена, 2026',
      exampleTo: 'грузоперевозки из китая, авиадоставка, таможенное оформление',
      tips: [
        'Время, потраченное на это поле, лучше вложить в «Короткое описание для Google» — оно действительно работает.',
        'Ключевые слова работают внутри заголовка и текста. Не в этом теге.',
        'Оставите пустым — ничего не потеряете.',
      ],
      warn: 'Поле добавлено по вашему запросу и выводится на сайт. Но на позиции в поиске оно не влияет — пишем об этом прямо, чтобы вы это знали.',
    },
    languages: {
      title: 'Как устроены языки',
      what: 'Каждый язык заполняется отдельно: заголовок, текст и тексты для Google хранятся независимо.',
      how: 'Узбекский и русский — обязательны. Английский — по желанию.',
      exampleLabel: 'Четыре точки',
      exampleFrom: 'заголовок · текст · текст для Google · изображение',
      exampleTo: 'закрашенная точка — заполнено, пустая — ещё нет',
      tips: [
        'Чтобы переключить язык, нажмите на его плашку сверху.',
        'Плашка пунктиром — язык ещё не заполнен. Это не ошибка.',
        'Если английского нет, на сайте покажется русский текст.',
      ],
      warn: 'Страница без английского не добавляется в Google — так один и тот же текст не попадёт в выдачу дважды.',
    },
    cover: {
      title: 'Обложка',
      what: 'Изображение в списке новостей и вверху самой страницы.',
      how: 'Соотношение 16:9, ширина от 1200 пикселей, до 10 МБ.',
      exampleLabel: 'Рекомендация',
      exampleFrom: 'Скриншот, баннер с текстом',
      exampleTo: 'Настоящая фотография: контейнер, склад, самолёт',
      tips: [
        'Заполните описание изображения («alt») — его читает Google.',
        'Не пишите текст поверх картинки: на телефоне его не прочитать.',
      ],
      warn: '',
    },
    canonical: {
      title: 'Основной адрес',
      what: 'Если этот текст опубликован и на другом сайте, укажите здесь адрес оригинала.',
      how: 'Полный адрес, начиная с https://.',
      exampleLabel: 'Например',
      exampleFrom: '/news/napravlenie',
      exampleTo: 'https://ipost.uz/ru/news/novoe-napravlenie-iz-kitaya',
      tips: ['Почти всегда остаётся пустым. Если не уверены — не трогайте.'],
      warn: '',
    },
    noIndex: {
      title: 'Не добавлять в Google',
      what: 'Страница останется на сайте и откроется по ссылке, но не появится в поиске.',
      how: 'Для временных и внутренних страниц.',
      exampleLabel: 'Когда нужно',
      exampleFrom: 'Обычная новость',
      exampleTo: 'Страница акции, которую отправляют только клиенту',
      tips: ['Для обычной новости это не нужно.'],
      warn: '',
    },
    close: 'Понятно',
  },

  stamp: {
    complete: 'заполнено',
    seoLeft: 'осталось SEO',
    empty: 'не заполнено',
    switchTo: 'перейти к этому языку',
    dotTitle: 'заголовок',
    dotBody: 'текст',
    dotSeo: 'текст для Google',
    dotCover: 'изображение',
  },

  services: {
    title: 'Услуги',
    subtitle: 'Отдельная страница для каждой услуги',
    new: 'Добавить услугу',
    emptyTitle: 'Страниц услуг пока нет',
    emptyHint: 'Создайте отдельную страницу для каждой услуги — они соберутся в список на сайте.',
    emptyAction: 'Добавьте первую услугу',
    excerptLabel: 'Короткое описание',
    excerptHint: 'Этот текст виден в списке услуг.',
    iconLabel: 'Значок',
    orderHint: 'Порядок меняется перетаскиванием.',
    deleteConfirm: 'Удалить эту услугу?',
    deleteConfirmBody: 'Услуга исчезнет с сайта. Её можно будет вернуть.',
    slugRequired: 'Сначала введите заголовок или адрес страницы.',
  },

  stories: {
    title: 'Истории',
    subtitle: 'Кружочки историй для мобильного приложения',
    new: 'Новая история',
    emptyTitle: 'Историй пока нет',
    emptyHint: 'Создайте первую историю — она появится кружочком в приложении.',
    emptyAction: 'Создайте первую историю',

    titleLabel: 'Заголовок',
    titleHint: 'Название под кружочком истории.',
    coverLabel: 'Обложка',
    coverHint: 'Изображение внутри кружочка. Если пусто — берётся первый слайд.',
    ttlLabel: 'Длительность',
    ttlHint: 'После публикации история исчезнет из приложения через это время.',
    ttlOption: (n: number) => (n % 24 === 0 ? `${n} ч (${n / 24} дн.)` : `${n} ч`),
    orderLabel: 'Порядок',
    orderHint: 'Меньшее число идёт раньше.',

    slidesGroup: 'Слайды',
    slidesHint: 'Зритель листает их по очереди.',
    addSlide: 'Добавить слайд',
    removeSlide: 'Удалить слайд',
    moveUp: 'Вверх',
    moveDown: 'Вниз',
    slideN: (n: number) => `Слайд ${n}`,
    noSlides: 'Слайдов пока нет. Добавьте хотя бы один.',

    mediaTypeImage: 'Изображение',
    mediaTypeVideo: 'Видео',
    mediaLabel: 'Медиа',
    mediaEmpty: 'Выберите медиа',
    mediaChange: 'Заменить',
    posterLabel: 'Первый кадр',
    posterHint: 'Изображение, пока грузится видео.',
    posterEmpty: 'Выберите изображение',
    captionLabel: 'Подпись',
    buttonGroup: 'Кнопка',
    buttonHint: 'Появится кнопкой внизу слайда',
    buttonOn: 'Включена',
    buttonOff: 'Выключена',
    buttonNeedsUrl: 'Укажите ссылку, чтобы кнопка работала',
    linkUrlLabel: 'Ссылка кнопки',
    linkUrlHint: 'Полный адрес (https://…) или путь внутри приложения.',
    linkLabelLabel: 'Текст кнопки',
    durationLabel: 'Длительность',
    durationSuffix: 'сек',

    views: 'Просмотры',
    expired: 'Срок истёк',
    slideCount: (n: number) => {
      const mod10 = n % 10;
      const mod100 = n % 100;
      if (mod10 === 1 && mod100 !== 11) return `${n} слайд`;
      if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} слайда`;
      return `${n} слайдов`;
    },
    hours: (n: number) => `${n} ч`,

    deleteConfirm: 'Удалить эту историю?',
    deleteConfirmBody: 'История исчезнет из приложения. Её можно будет вернуть.',

    blockerNoSlides: 'Добавьте хотя бы один слайд',
    blockerSlideMedia: (n: number) => `Для слайда ${n} не выбрано медиа`,

    titlePlaceholder: 'Например: Новый авиарейс',
    publishBlocked: 'Для публикации нужен хотя бы один слайд с медиа',
    previewLabel: 'Предпросмотр',
    previewNow: 'сейчас',
    previewEmpty: 'Выберите медиа',
    previewHint: 'Так это выглядит в приложении. Выберите слайд для просмотра.',

    // Фильтр витрины.
    filterAll: 'Все',
    filterPublished: 'Опубликованные',
    filterUnpublished: 'Неопубликованные',
    filterEmpty: 'В этом статусе историй нет',

    // Слова состояния/действия для приложения (не для сайта).
    statusLive: 'В приложении',
    statusDraft: 'Черновик',
    publish: 'Опубликовать',
    unpublish: 'Убрать из приложения',
    unpublishHint: 'История временно убирается из приложения. Потом можно опубликовать снова.',
    published: 'История опубликована в приложении',

    uploadMedia: 'Фото/видео',
    captionHint: 'Короткий текст поверх слайда (необязательно).',
    captionPlaceholder: 'Например: Шэньчжэнь–Ташкент за 3 дня',
    linkLabelPlaceholder: 'Например: Подробнее',
  },

  pages: {
    title: 'Страницы',
    subtitle: 'Тексты для Google на постоянных страницах сайта',
    empty: 'Не заполнено',
    filled: 'Заполнено',
    partial: 'Частично',
    groupMain: 'Основные страницы',
    groupMainHint: 'Самые важные страницы сайта — заполните их в первую очередь',
    groupMore: 'Дополнительные страницы',
    groupMoreHint: 'Вспомогательные и правовые страницы',
    summaryTitle: 'Заполнение SEO',
    summary: (filled: number, total: number) => `Из ${total} страниц заполнено ${filled}`,
    openHint: 'Нажмите, чтобы открыть',
    headingLabel: 'Заголовок страницы',
    seoTextLabel: 'SEO-текст',
    seoTextHint:
      'Этот текст выводится внизу страницы, перед футером. Можно использовать заголовки, списки и ссылки.',
    routeNames: {
      '/': 'Главная',
      '/about': 'О компании',
      '/services': 'Услуги',
      '/news': 'Новости',
      '/contact': 'Контакты',
      '/offices': 'Офисы',
      '/track': 'Отслеживание',
      '/careers': 'Вакансии',
      '/media': 'Пресса',
      '/privacy': 'Политика конфиденциальности',
      '/terms': 'Условия использования',
    },
  },

  categories: {
    title: 'Категории',
    subtitle: 'У каждой категории своя страница на сайте',
    new: 'Добавить категорию',
    nameLabel: 'Название категории',
    keyLabel: 'Системное имя',
    keyHint: 'Не меняется. По нему сайт отбирает новости.',
    articleCount: 'новостей',
    countLabel: (n: number) => {
      const mod10 = n % 10;
      const mod100 = n % 100;
      if (mod10 === 1 && mod100 !== 11) return `${n} новость`;
      if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} новости`;
      return `${n} новостей`;
    },
    emptyTitle: 'Категорий нет',
    emptyHint: 'Категории группируют новости, и у каждой появляется своя страница.',
    emptyAction: 'Добавьте первую категорию',
    deleteHasArticles: 'В этой категории есть новости. Сначала перенесите их в другую.',
  },

  media: {
    title: 'Изображения',
    subtitle: 'Изображения для новостей и услуг',
    upload: 'Загрузить',
    uploading: 'Загружается…',
    dropHere: 'Перетащите изображение сюда',
    dropHint: 'или нажмите, чтобы выбрать · JPEG, PNG, WebP · до 10 МБ',
    dropHintVideo: 'или нажмите, чтобы выбрать · JPEG, PNG, WebP или MP4 · до 10 МБ',
    video: 'Видео',
    emptyTitle: 'Изображений пока нет',
    emptyHint: 'Чтобы поставить обложку новости, сначала загрузите изображение.',
    emptyAction: 'Загрузите первое изображение',
    filterAll: 'Все',
    filterUnused: 'Неиспользуемые',
    pick: 'Выбрать',
    inUse: 'используется',
    unused: 'не используется',
    usedBy: 'Используется здесь',
    deleteInUse: 'Это изображение используется. Сначала замените его.',
    tooLarge: (mb: number) => `Изображение слишком большое (${mb} МБ). Максимум 10 МБ.`,
    wrongType: 'Такой тип файла не принимается. Загрузите JPEG, PNG или WebP.',
    uploadFailed: 'Не загрузилось',
    dimensions: 'размер',
  },

  redirects: {
    title: 'Перенаправления',
    subtitle: 'Перевод со старых адресов на новые',
    new: 'Добавить перенаправление',
    from: 'Старый адрес',
    to: 'Новый адрес',
    fromHint: 'Например /ru/news/staroe-imya',
    code: 'Тип',
    code301: '301 — перенесено навсегда',
    code302: '302 — временно',
    code308: '308 — навсегда (метод сохраняется)',
    active: 'Работает',
    turnOn: 'Включить перенаправление',
    turnOff: 'Выключить перенаправление',
    automatic: 'автоматически',
    manual: 'вручную',
    emptyTitle: 'Перенаправлений нет',
    emptyHint:
      'Когда вы меняете адрес страницы, перенаправление создаётся само. Здесь можно добавить и вручную.',
    emptyAction: 'Добавьте перенаправление',
    selfRedirect: 'Адрес не может вести сам на себя.',
  },

  scans: {
    // Режим поиска (главный экран консоли)
    consoleTitle: 'RS Телеметрия',
    consoleKicker: 'Контроль сканирования',
    backToCms: 'Вернуться в CMS',
    searchHeading: 'Какой номер проверяем?',
    searchLead: 'Введите номер коробки или трека — увидите, где, кем и каким способом он был отсканирован.',
    searchAction: 'Найти',
    browseAction: 'Открыть весь список',
    newSearch: 'Новый поиск',
    resultsFor: 'Результат',
    scanCount: 'сканов',
    firstScan: 'Первый скан',
    lastScan: 'Последний скан',
    operators: 'Сотрудники',
    timeline: 'История сканирований',
    notFoundTitle: 'По этому номеру сканов нет',
    notFoundHint: 'Проверьте номер и попробуйте снова или поищите в общем списке.',
    searching: 'Поиск…',
    todayScans: 'Сегодня',
    liveLabel: 'Онлайн',
    filters: 'Фильтры',
    table: 'Таблица',
    map: 'Карта',
    showing: 'показано',
    of: 'из',
    title: 'Сканирования RS',
    subtitle: 'Где, каким способом и когда отсканировано — журнал контроля',
    clearFilters: 'Сбросить фильтры',
    export: 'Скачать CSV',
    searchPlaceholder: 'Номер коробки или трека',
    allOperators: 'Все сотрудники',
    allKinds: 'Все операции',
    allSources: 'Все способы',
    range: { today: 'Сегодня', week: '7 дней', month: '30 дней', all: 'Всё время' },
    sourcePda: 'ТСД лазер',
    sourceManual: 'Вручную',
    sourceCamera: 'Камера',
    kindReceive: 'Приём RS',
    kindSorting: 'Сортировка',
    kindSticker: 'Стикер',
    resultOk: 'Успешно',
    resultFailed: 'Ошибка',
    suspicious: 'Подозрительные',
    statTotal: 'Всего сканов',
    statOk: 'Успешно',
    statFailed: 'Ошибки',
    statLocated: 'С геопозицией',
    statSuspicious: 'Подозрительные',
    withoutLocation: 'без геопозиции',
    mapTitle: 'Места сканирования',
    mapHint: 'Масштаб — Ctrl + колесо или кнопки +/−. Нажмите на точку, чтобы увидеть детали.',
    gridMode: 'Режим сетки',
    mockedGps: 'Поддельный GPS',
    noFix: 'Нет',
    noFixHint: 'Во время скана не было сигнала GPS. В закрытом складе это норма — сканирование не блокируется.',
    emptyTitle: 'Сканы не найдены',
    emptyHint: 'За выбранный период и фильтры сканов нет. Попробуйте сбросить фильтры.',
    prev: 'Назад',
    next: 'Вперёд',
    operator: 'Сотрудник',
    role: 'Роль',
    location: 'Геопозиция',
    accuracy: 'Точность',
    openInMap: 'Открыть на карте',
    time: 'Время',
    serverTime: 'Время сервера',
    deviceTime: 'Время устройства',
    clockSkew: 'Расхождение часов',
    minutes: 'мин',
    duration: 'Время ответа',
    device: 'Устройство',
    serverError: 'Сообщение сервера',
  },

  leads: {
    title: 'Обращения',
    subtitle: 'Заявки с формы на сайте',
    emptyTitle: 'Обращений пока нет',
    emptyHint: 'Заявки появятся здесь, как только кто-то заполнит форму на сайте.',
    name: 'Имя',
    contact: 'Телефон',
    message: 'Сообщение',
    source: 'Откуда',
    received: 'Получено',
    markRead: 'Отметить прочитанным',
    markUnread: 'Отметить непрочитанным',
    export: 'Скачать CSV',
    statusNew: 'Новое',
    statusContacted: 'Связались',
    statusQualified: 'Подходит',
    statusClosed: 'Закрыто',
    statusSpam: 'Спам',
    campaign: 'Кампания',
    landingPage: 'С какой страницы',
  },

  users: {
    title: 'Пользователи',
    subtitle: 'Кто может входить в систему',
    new: 'Добавить пользователя',
    name: 'Имя',
    username: 'Логин',
    usernameHint: 'Логин для входа — латиница, цифры, . _ - (например: dilshod или smm.manager).',
    email: 'Эл. почта',
    emailHint: 'Необязательно. Можно входить и по email вместо логина.',
    password: 'Пароль',
    passwordHint: 'Минимум 10 символов. Пользователь входит с этим логином и паролем.',
    role: 'Роль',
    roleHint: 'Роль определяет, какие страницы доступны пользователю.',
    noRole: 'Роль не выбрана',
    lastLogin: 'Последний вход',
    neverLoggedIn: 'ещё не входил',
    active: 'Активен',
    disabled: 'Отключён',
    resetPassword: 'Выдать новый пароль',
    resetPasswordBody:
      'Новый пароль показывается один раз. Передайте его пользователю сами — увидеть его снова не получится.',
    issuedPassword: 'Новый пароль',
    issuedPasswordHint:
      'Скопируйте и передайте пользователю. При входе система попросит его сменить.',
    disable: 'Отключить',
    enable: 'Включить',
    delete: 'Удалить',
    deleteBody:
      'Пользователь будет удалён полностью и больше не сможет войти. Это действие необратимо.',
    deleteSelf: 'Нельзя удалить собственную учётную запись',
    lastAdmin: 'Это единственный администратор в системе.',
  },

  roles: {
    title: 'Роли',
    subtitle: 'Выберите, какие страницы доступны каждой роли',
    new: 'Создать роль',
    edit: 'Редактировать роль',
    name: 'Название роли',
    nameHint: 'Например: SMM-менеджер, Контент-редактор, Контролёр склада.',
    description: 'Описание',
    descriptionHint: 'Необязательно — коротко опишите, для кого эта роль.',
    permissions: 'Разрешённые страницы',
    permissionsHint: 'Пользователь видит и открывает только отмеченные страницы.',
    permissionsCount: (n: number) => `${n} стр.`,
    users: 'Пользователи',
    usersCount: (n: number) => `${n} польз.`,
    system: 'Системная роль',
    superAdmin: 'Главный администратор',
    superAdminNote: 'Главный администратор имеет доступ ко всем страницам — изменить нельзя.',
    delete: 'Удалить роль',
    deleteBody: 'Роль будет удалена полностью. Это действие необратимо.',
    deleteHasUsers: 'В этой роли есть пользователи. Сначала переведите их на другую роль.',
    deleteSystem: 'Системную роль удалить нельзя.',
    empty: 'Ролей пока нет',
    selectAll: 'Выбрать все',
    clearAll: 'Очистить',
  },

  pageLabels: {
    stories: 'Истории',
    media: 'Медиа',
    pages: 'Страницы',
    articles: 'Новости',
    services: 'Услуги',
    categories: 'Категории',
    redirects: 'Редиректы',
    leads: 'Обращения',
    scans: 'RS-сканеры',
    audit: 'История изменений',
    users: 'Пользователи и роли',
  } as Record<string, string>,

  audit: {
    title: 'История изменений',
    subtitle: 'Кто, что и когда изменил',
    who: 'Кто',
    what: 'Что',
    when: 'Когда',
    field: 'Поле',
    from: 'Было',
    to: 'Стало',
    empty: 'Изменений пока нет',
    actions: {
      'article.create': 'добавил(а) новость',
      'article.update': 'изменил(а) новость',
      'article.publish': 'опубликовал(а) новость',
      'article.unpublish': 'снял(а) новость с сайта',
      'article.delete': 'удалил(а) новость',
      'article.restore': 'вернул(а) новость',
      'service.create': 'добавил(а) услугу',
      'service.update': 'изменил(а) услугу',
      'service.publish': 'опубликовал(а) услугу',
      'service.unpublish': 'снял(а) услугу с сайта',
      'service.delete': 'удалил(а) услугу',
      'category.create': 'добавил(а) категорию',
      'category.update': 'изменил(а) категорию',
      'category.delete': 'удалил(а) категорию',
      'category.reorder': 'изменил(а) порядок категорий',
      'page.update': 'изменил(а) страницу',
      'media.upload': 'загрузил(а) изображение',
      'media.delete': 'удалил(а) изображение',
      'media.alt': 'изменил(а) описание изображения',
      'redirect.create': 'добавил(а) перенаправление',
      'redirect.update': 'изменил(а) перенаправление',
      'redirect.delete': 'удалил(а) перенаправление',
      'user.create': 'добавил(а) пользователя',
      'user.update': 'изменил(а) пользователя',
      'user.role_change': 'изменил(а) права',
      'user.reset_password': 'выдал(а) новый пароль',
      'user.deactivate': 'отключил(а) пользователя',
      'user.delete': 'удалил(а) пользователя',
      'role.create': 'создал(а) роль',
      'role.update': 'изменил(а) роль',
      'role.delete': 'удалил(а) роль',
      'auth.login': 'вошёл(ла) в систему',
      'auth.change_password': 'сменил(а) пароль',
      'lead.update': 'изменил(а) обращение',
      'lead.delete': 'удалил(а) обращение',
      'lead.export': 'выгрузил(а) обращения',
    },
  },

  palette: {
    placeholder: 'Найдите или выберите действие…',
    noResults: 'Ничего не найдено',
    sections: { go: 'Перейти', create: 'Создать', actions: 'Действия' },
    toggleTheme: 'Сменить тему',
    switchLang: 'Сменить язык интерфейса',
  },

  errors: {
    network: 'Не удалось связаться с сервером. Проверьте интернет и повторите.',
    unknown: 'Произошла непредвиденная ошибка.',
    notFound: 'Такой страницы нет',
    notFoundHint: 'Возможно, адрес указан неверно.',
    goHome: 'Вернуться на панель',
    forbidden: 'У вас нет доступа к этому разделу',
    forbiddenHint: 'При необходимости попросите администратора.',
  },

  theme: {
    light: 'Светлая',
    dark: 'Тёмная',
    system: 'Как в системе',
    switchToLight: 'Переключить на светлую тему',
    switchToDark: 'Переключить на тёмную тему',
  },
};
